import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {makeAutoObservable, observable} from "mobx";
import {ModalType, RequestState} from "data/enums";
import {Bindings} from "data/constants/bindings";
import type {IUserStore} from "data/stores/user/user.store";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IModalsStore} from "data/stores/modals/modals.store";
import type {ILineup, ITeamStore} from "data/stores/team/team.store";
import type {IRoundsStore, ISession, IRound} from "data/stores/rounds/rounds.store";
import {every, get} from "lodash";

export interface ISaveBarController extends ViewController {
	get i18n(): ILocalizationStore;
	get lineup(): ILineup;
	get isCurrentLineupFull(): boolean;
	get isCurrentLineupHasPlayer(): boolean;
	get selectedRound(): IRound | undefined;
	get selectedSession(): ISession | undefined;
	get isTeamSaved(): boolean;
	get isTeamChangedNotSaved(): boolean;
	get isTradeOpen(): boolean;
	get tradesArr(): number[];
	get isLoadingTeam(): boolean;

	onAutoPick: () => void;
	onClearLineup: () => void;
	onSaveTeam: () => void;
	onRollbackTrades: () => void;
	onSaveTrades: () => void;
	openTutorial: () => void;
}

@injectable()
export class SaveBarController implements ISaveBarController {
	@observable _requestState: RequestState = RequestState.IDLE;

	get i18n() {
		return this._i18nStore;
	}

	get lineup() {
		return this._teamStore.lineup;
	}

	get isCurrentLineupFull() {
		return this._teamStore.isCurrentLineupFull;
	}

	get isCurrentLineupHasPlayer() {
		return this._teamStore.isCurrentLineupHasPlayer;
	}

	get selectedRound() {
		return this._roundStore.selectedRound;
	}

	get selectedSession() {
		return this._roundStore.selectedSession;
	}

	get isTeamSaved() {
		return this._teamStore.isTeamSaved;
	}

	get isTeamChangedNotSaved() {
		return this._teamStore.isTeamChangedNotSaved;
	}

	get isTradeOpen() {
		return every([this._roundStore.isPMSession, this._teamStore.isTradeStarted]);
	}

	get tradesArr() {
		return this._teamStore.tradesArr;
	}

	get isLoadingTeam() {
		return this._teamStore.isLoadingTeam;
	}

	constructor(
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.LocalizationStore) private _i18nStore: ILocalizationStore,
		@inject(Bindings.ModalsStore) private _modalStore: IModalsStore,
		@inject(Bindings.RoundsStore) private _roundStore: IRoundsStore,
		@inject(Bindings.TeamStore) private _teamStore: ITeamStore
	) {
		makeAutoObservable(this);
	}

	onAutoPick = () => {
		void this._teamStore.autoPickTeam({
			lineup: this._teamStore.lineup,
			gamedayId: this._roundStore.selectedRoundId,
		});
	};

	onClearLineup = () => {
		this._teamStore.clearLineup();
	};

	onSaveTeam = () => {
		const hasCaptain = get(this._teamStore.lineup, "captain", 0);

		if (!hasCaptain) {
			this._modalStore.showModal(ModalType.ERROR, {
				title: this._i18nStore.t(
					"modals.error.captain_not_picked_title",
					"Select your captain"
				),
				message: this._i18nStore.t(
					"modals.error.captain_not_picked",
					"You must select a captain via the 'C' icon"
				),
			});
		} else {
			void this._teamStore.setTeam({
				lineup: this._teamStore.lineup,
				gamedayId: this._roundStore.selectedRoundId,
			});
		}
	};

	onRollbackTrades = () => {
		void this._teamStore.onRollbackLocal();
	};

	onSaveTrades = () => {
		const hasCaptain = get(this._teamStore.lineup, "captain", 0);

		if (!hasCaptain) {
			this._modalStore.showModal(ModalType.ERROR, {
				title: this._i18nStore.t(
					"modals.error.captain_not_picked_title",
					"Select your captain"
				),
				message: this._i18nStore.t(
					"modals.error.captain_not_picked",
					"You must select a captain via the 'C' icon"
				),
			});
		} else {
			this._modalStore.showModal(ModalType.TRADES_CONFIRM, {
				title: this._i18nStore.t("modals.trades_confirm.title", "TEAM TRADES"),
				message: this._i18nStore.t(
					"modals.trades_confirm.description",
					"Are you sure that you want to trade you players?"
				),
			});
		}
	};

	openTutorial = () => {
		this._modalStore.showModal(ModalType.TUTORIAL, {
			message: " ",
		});
	};

	dispose() {
		return;
	}

	init(param: void) {
		return;
	}
}
